import React, { useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { toast, Toaster } from "sonner";

const ContactPage = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contacts", form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full bg-[#FDFBF7] border border-[#E8DFD8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5A3C]/30 focus:border-[#8B5A3C] transition-all";
  const labelClass = "block text-[11px] uppercase tracking-[0.18em] font-bold text-[#5C544B] mb-2";

  return (
    <div data-testid="contact-page" className="fade-in">
      <Toaster position="top-right" richColors />
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28 grid lg:grid-cols-2 gap-16">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-[#8B5A3C] mb-5">{t.contact.eyebrow}</p>
          <h1 className="font-display text-5xl sm:text-6xl font-medium leading-[1.05] tracking-tight text-[#2C2621] whitespace-pre-line mb-6">
            {t.contact.title}
          </h1>
          <p className="text-[#5C544B] text-lg leading-relaxed mb-12">{t.contact.subtitle}</p>

          <ul className="space-y-7">
            <li className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F3E6DA] flex items-center justify-center shrink-0"><MapPin size={18} className="text-[#8B5A3C]" /></div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A8178] mb-1">{t.contact.address}</div>
                <div className="text-[#2C2621] font-display text-xl">Via Livia Gereschi, 20</div>
                <div className="text-[#5C544B]">56124 Pisa (PI)</div>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F3E6DA] flex items-center justify-center shrink-0"><Phone size={18} className="text-[#8B5A3C]" /></div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A8178] mb-1">{t.contact.phone}</div>
                <a href="tel:+393757380501" className="text-[#2C2621] font-display text-xl hover:text-[#8B5A3C] transition-colors">+39 375 738 0501</a>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F3E6DA] flex items-center justify-center shrink-0"><Mail size={18} className="text-[#8B5A3C]" /></div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A8178] mb-1">{t.contact.email}</div>
                <a href="mailto:segreteria@lasalaimmobiliare.it" className="text-[#2C2621] font-display text-xl hover:text-[#8B5A3C] transition-colors break-all">segreteria@lasalaimmobiliare.it</a>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F3E6DA] flex items-center justify-center shrink-0"><Clock size={18} className="text-[#8B5A3C]" /></div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A8178] mb-1">{t.contact.hours}</div>
                <div className="text-[#5C544B] whitespace-pre-line">{t.contact.hoursValue}</div>
              </div>
            </li>
          </ul>
        </div>

        <div className="lg:pl-10">
          <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-[#E8DFD8] rounded-[2rem] p-8 md:p-10 shadow-sm">
            <h2 className="font-display text-3xl text-[#2C2621] mb-8">{t.contact.formTitle}</h2>
            <div className="space-y-5">
              <div>
                <label className={labelClass}>{t.contact.name}</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} data-testid="contact-form-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>{t.contact.email}</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} data-testid="contact-form-email" />
                </div>
                <div>
                  <label className={labelClass}>{t.evaluation.phone}</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} data-testid="contact-form-phone" />
                </div>
              </div>
              <div>
                <label className={labelClass}>{t.contact.message}</label>
                <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputClass} resize-none`} data-testid="contact-form-message" />
              </div>
              <button disabled={submitting} type="submit" data-testid="contact-form-submit" className="w-full bg-[#8B5A3C] text-white hover:bg-[#704830] disabled:opacity-60 transition-colors rounded-full py-3.5 font-medium">
                {submitting ? "…" : t.contact.submit}
              </button>
            </div>
          </form>
        </div>
      </section>

      <section className="bg-[#F3E6DA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-2">
          <iframe
            title="La Sala Immobiliare - Pisa"
            src="https://www.google.com/maps?q=Via+Livia+Gereschi+20+Pisa&output=embed"
            className="w-full h-96 rounded-[2rem] my-10 border-0"
            loading="lazy"
          />
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
